<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import axios from 'axios';

import Button from '../../../../components-next/button/Button.vue';
import FormSection from '../../../../components/FormSection.vue';
import AlertResponza from '../../../../components-next/responza/AlertResponza.vue';
import AgentBasicInfo from '../components/create-agentis/AgentBasicInfo.vue';
import CompanyInfo from '../components/create-agentis/CompanyInfo.vue';
import UserDataFieldsWrapper from '../components/create-agentis/UserDataFieldsWrapper.vue';
import PersonalitySelector from '../components/create-agentis/PersonalitySelector.vue';
import ConversationFlowSelector from '../components/create-agentis/ConversationFlowSelector.vue';
import AdditionalInfo from '../components/create-agentis/AdditionalInfo.vue';
import TagsDataFieldsWrapper from '../components/create-agentis/TagsDataFieldsWrapper.vue';
import TeamsDataFieldsWrapper from '../components/create-agentis/TeamsDataFieldsWrapper.vue';
import HumanRedirectField from '../components/create-agentis/HumanRedirectField.vue';
import FlowSpecificConfig from '../components/create-agentis/FlowSpecificConfig.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

// Determinar se estamos no modo de edição
const isEditMode = ref(route.query.mode === 'edit');
const agentId = ref(route.query.id ? parseInt(route.query.id) : null);

// State
const isSubmitting = ref(false);
const showInitialPage = ref(true); // Nova página inicial
const activeStep = ref(1);
const previousStep = ref(1); // Track previous step for animation direction
const highestStepReached = ref(1); // Track the highest step reached
const isTransitioning = ref(false); // Track transition state
const isInitialLoad = ref(true); // Track initial page load

// Estados para controle de mudanças não salvas
const hasUnsavedChanges = ref(false);
const showExitConfirmation = ref(false);
const pendingRoute = ref(null);
const isFormStarted = ref(false); // Indica se o usuário começou a preencher o formulário

// Número total de passos
const totalSteps = computed(() => {
  // Habilitar passo 10 para fluxo de vendas (id 2) e agendamento (id 3)
  const hasFlowSpecificConfig = agentData.conversationFlowId === 2 || agentData.conversationFlowId === 3;
  return hasFlowSpecificConfig ? 10 : 9;
});

// Estados de validação dos componentes filhos
const agentNameIsValid = ref(false);
const humanRedirectIsValid = ref(true); // Estado de validação para redirecionamento humano (campo opcional)
const companyInfoIsValid = ref(false);
const additionalInfoIsValid = ref(true); // Novo estado de validação
const tagsDataIsValid = ref(true); // Estado de validação para tags
const teamsDataIsValid = ref(true); // Estado de validação para times
const userFieldsIsValid = ref(true);
const flowSpecificConfigIsValid = ref(true); // Estado de validação para configurações específicas do fluxo

// Erros de validação
const validationErrors = reactive({
  name: '',
  role: '',
  company: {
    name: '',
    website: '',
    description: '',
    support: {
      email: '',
      phone: ''
    }
  },
  userFields: []
});

// Dados persistidos entre etapas
const agentData = reactive({
  name: '',
  voice: 'female', // default value
  role: '', // Função do agente
  messageGrouping: 25, // Tempo de agrupamento de mensagens (0, 15, 25, 35)
  audioEnabled: true, // Campo audioEnabled
  randomness: 40, // Campo randomness (aleatoriedade)
  customImageInstructionsEnabled: false, // Campo para habilitar instruções customizadas de imagem
  imagePrompt: '', // Campo para prompt de IA de leitura de imagem
  humanRedirectConditions: '', // Campo humanRedirectConditions
  humanAvailabilityMessagingEnabled: false, // Campo para habilitar mensagens personalizadas de disponibilidade humana
  humanAvailabilityHours: { // Campo para horários de disponibilidade humana - ordem igual ao profissional
    sunday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
    monday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
    tuesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
    wednesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
    thursday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
    friday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
    saturday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] }
  },
  company: {
    name: '',
    cnpj: '', // Novo campo CNPJ
    website: '',
    description: '', // Campo de descrição da empresa
    locations: [], // Array de objetos de endereço
    support: {
      phone: '',
      email: '',
      faqLink: ''
    },
    policies: []
  },
  additionalInfo: [], // Movido para fora de company
  tagFields: [], // Campos de tags baseados nas labels do account
  teamFields: [], // Campos de times baseados nos teams do account
  userFields: [], // Campos são opcionais, inicializa vazio
  personalityId: null,
  conversationFlowId: null,
  flowSpecificConfig: {} // Configurações específicas do fluxo selecionado
});

// Funções auxiliares para simplificar o carregamento de dados
const normalizeLocations = (locations) => {
  if (!locations) return [];
  const locArray = Array.isArray(locations) ? locations : [];
  
  return locArray.map(loc => {
    const location = { ...loc };
    // Converter cep para zipCode se necessário
    if (location.cep && !location.zipCode) {
      location.zipCode = location.cep;
      delete location.cep;
    }
    return location;
  });
};

const getFieldWithFallback = (...fields) => {
  for (const field of fields) {
    if (Array.isArray(field)) {
      return [...field];
    }
  }
  return [];
};

// Variável para controlar carregamento inicial de dados
let isSettingInitialData = false;

// Função corrigida para carregar os dados do agente para edição
const loadAgentForEditing = () => {
  if (!isEditMode.value) return;
  
  try {
    isSettingInitialData = true; // Prevent marking as changed during load
    
    // Recuperar dados do sessionStorage
    const storedAgentData = sessionStorage.getItem('editingAgent');
    if (!storedAgentData) {
      console.error('Dados do agente não encontrados no sessionStorage');
      isSettingInitialData = false;
      return;
    }
    
    const editingAgent = JSON.parse(storedAgentData);
    
    // Atualizar o ID do agente
    agentId.value = editingAgent.id;
    
    // Preencher os dados básicos
    agentData.name = editingAgent.name || '';
    agentData.voice = editingAgent.voice || 'female';
    
    // Preencher dados da empresa
    if (editingAgent.company_data) {
      let companyData = editingAgent.company_data;
      
      // Se for string, converte para objeto
      if (typeof companyData === 'string') {
        try {
          companyData = JSON.parse(companyData);
        } catch (e) {
          companyData = {};
        }
      }
      
      // Carregar messageGrouping de dentro do company_data
      if (companyData.messageGrouping !== undefined) {
        agentData.messageGrouping = companyData.messageGrouping;
      }
      
      // Carregar audioEnabled, randomness, customImageInstructionsEnabled, imagePrompt e humanRedirectConditions
      if (companyData.audioEnabled !== undefined) {
        agentData.audioEnabled = companyData.audioEnabled;
      }
      if (companyData.randomness !== undefined) {
        agentData.randomness = companyData.randomness;
      }
      if (companyData.customImageInstructionsEnabled !== undefined) {
        agentData.customImageInstructionsEnabled = companyData.customImageInstructionsEnabled;
      }
      if (companyData.imagePrompt !== undefined) {
        agentData.imagePrompt = companyData.imagePrompt;
      }
      if (companyData.humanRedirectConditions !== undefined) {
        agentData.humanRedirectConditions = companyData.humanRedirectConditions;
      }
      if (companyData.humanAvailabilityMessagingEnabled !== undefined) {
        agentData.humanAvailabilityMessagingEnabled = companyData.humanAvailabilityMessagingEnabled;
      }
      if (companyData.humanAvailabilityHours !== undefined) {
        // Se for string, fazer parse para objeto
        if (typeof companyData.humanAvailabilityHours === 'string') {
          try {
            agentData.humanAvailabilityHours = JSON.parse(companyData.humanAvailabilityHours);
          } catch (e) {
            console.error('Erro ao fazer parse de humanAvailabilityHours:', e);
            // Em caso de erro, usar horários padrão
            agentData.humanAvailabilityHours = {
              sunday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
              monday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
              tuesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
              wednesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
              thursday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
              friday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
              saturday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] }
            };
          }
        } else {
          // Se já for objeto, usar diretamente
          agentData.humanAvailabilityHours = companyData.humanAvailabilityHours;
        }
      } else if (!companyData.humanAvailabilityHours) {
        // Se não houver horários definidos, manter o padrão
        agentData.humanAvailabilityHours = {
          sunday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
          monday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
          tuesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
          wednesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
          thursday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
          friday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
          saturday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] }
        };
      }
      
      // Preencher os campos da empresa
      agentData.company = {
        name: companyData.name || '',
        cnpj: companyData.cnpj || '',
        website: companyData.website || '',
        description: companyData.description || '', // Campo de descrição da empresa
        locations: normalizeLocations(companyData.locations || companyData.location),
        support: {
          phone: companyData.support?.phone || '',
          email: companyData.support?.email || '',
          faqLink: companyData.support?.faqLink || ''
        },
        policies: Array.isArray(companyData.policies) 
          ? [...companyData.policies] 
          : []
      };
      
      
      // Extrair a função do agente do objeto company_data
      // Esta é a parte crítica - recuperamos o role de dentro da empresa
      if (companyData.role) {
        agentData.role = companyData.role;
      }
      
      // Carregar campos com fallback para formatos antigos
      agentData.additionalInfo = getFieldWithFallback(
        editingAgent.additionalInfo,
        companyData.additionalInfo,
        companyData.support?.additionalInfo
      );
      
      // Carregar campos de tags se existirem
      const tagFieldsData = getFieldWithFallback(editingAgent.tagFields, companyData.tagFields);
      agentData.tagFields = tagFieldsData ? transformTagFieldsForEditing(tagFieldsData) : [];
      
      // Carregar campos de times se existirem
      const teamFieldsData = getFieldWithFallback(editingAgent.teamFields, companyData.teamFields);
      agentData.teamFields = teamFieldsData ? transformTeamFieldsForEditing(teamFieldsData) : [];
      
      // Configurações de agendamento removidas - agora são gerenciadas no profissional
    }
    
    // Preencher campos de usuário
    if (editingAgent.user_fields) {
      let userFields = editingAgent.user_fields;
      
      // Se for string, converte para objeto
      if (typeof userFields === 'string') {
        try {
          userFields = JSON.parse(userFields);
        } catch (e) {
          userFields = [];
        }
      }
      
      agentData.userFields = Array.isArray(userFields) 
        ? [...userFields]
        : []; // Campos são opcionais, não adiciona campo padrão
    }
    
    // Preencher personalidade e fluxo de conversa
    agentData.personalityId = editingAgent.personality || null;
    agentData.conversationFlowId = editingAgent.flow || null;

    // Carregar configurações específicas do fluxo se existirem
    if (editingAgent.company_data) {
      let companyDataForCart = editingAgent.company_data;
      if (typeof companyDataForCart === 'string') {
        try {
          companyDataForCart = JSON.parse(companyDataForCart);
        } catch (e) {
          companyDataForCart = {};
        }
      }

      if (companyDataForCart.shoppingCartEnabled !== undefined && editingAgent.flow === 2) {
        agentData.flowSpecificConfig = { shoppingCartEnabled: companyDataForCart.shoppingCartEnabled };
      } else {
        agentData.flowSpecificConfig = {};
      }
    } else {
      agentData.flowSpecificConfig = {};
    }

    // Marcar validações iniciais como válidas
    // Vamos assumir que os dados carregados são válidos
    agentNameIsValid.value = !!agentData.name && !!agentData.role;
    companyInfoIsValid.value = !!agentData.company.name && !!agentData.company.description;
    additionalInfoIsValid.value = true; // Informações adicionais são opcionais
    tagsDataIsValid.value = true; // Tags são opcionais
    teamsDataIsValid.value = true; // Times são opcionais
    userFieldsIsValid.value = true;
    
    // Configurações de agendamento removidas
    
    console.log('Agente carregado com sucesso para edição');

    // NÃO limpar sessionStorage aqui - apenas no final do processo
  } catch (error) {
    console.error('Erro ao carregar dados do agente:', error);
    useAlert('Erro ao carregar dados do agente para edição', { type: 'error' });
  } finally {
    isSettingInitialData = false; // Re-enable change detection
  }
};

// Mapa unificado de validadores por etapa
const stepValidators = {
  1: () => agentNameIsValid.value,
  2: () => humanRedirectIsValid.value, // HumanRedirectField é opcional
  3: () => companyInfoIsValid.value,
  4: () => additionalInfoIsValid.value,
  5: () => tagsDataIsValid.value,
  6: () => teamsDataIsValid.value,
  7: () => userFieldsIsValid.value,
  8: () => agentData.personalityId !== null,
  9: () => agentData.conversationFlowId !== null,
  10: () => flowSpecificConfigIsValid.value, // Configurações específicas do fluxo
};

// Função unificada de validação
const validateStep = (stepNumber) => {
  // Se for o passo 10 e não for um fluxo com configurações específicas habilitadas, sempre retorna true
  // Habilitar para vendas (id 2) e agendamento (id 3)
  if (stepNumber === 10 && agentData.conversationFlowId !== 2 && agentData.conversationFlowId !== 3) {
    return true;
  }
  const validator = stepValidators[stepNumber];
  return validator ? validator() : false;
};

// Computed properties
const isFormValid = computed(() => {
  // Valida todos os passos de 1 até totalSteps
  for (let i = 1; i <= totalSteps.value; i++) {
    if (!validateStep(i)) {
      return false;
    }
  }
  return true;
});

// Computed property stepIsValid usando o mapa unificado
const stepIsValid = computed(() => {
  return validateStep(activeStep.value);
});

// Get authentication headers from cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) {
    return {};
  }
  try {
    const sessionInfo = JSON.parse(decodeURIComponent(cookie));
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao analisar cw_d_session_info');
    return {};
  }
};

// Função genérica para remover campos de erro de objetos
const removeErrorFields = (obj) => {
  const errorFieldSuffix = 'Error';
  const cleanObj = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (!key.endsWith(errorFieldSuffix)) {
      cleanObj[key] = value;
    }
  }
  
  return cleanObj;
};

// Função unificada para limpar arrays de campos
const cleanFieldsArray = (fields) => {
  if (!Array.isArray(fields)) return [];
  return fields.map(field => removeErrorFields(field));
};

// Helper functions to clean data before submission
const cleanCompanyData = (company) => {
  if (!company) return {};
  
  const cleanedCompany = JSON.parse(JSON.stringify(company)); // Deep clone
  
  // Garantir que os campos essenciais estão presentes
  cleanedCompany.name = company.name || '';
  cleanedCompany.cnpj = company.cnpj || '';
  cleanedCompany.website = company.website || '';
  cleanedCompany.description = company.description || '';
  
  // Clean locations items - remover campos de erro
  if (Array.isArray(cleanedCompany.locations)) {
    cleanedCompany.locations = cleanedCompany.locations.map(loc => {
      const cleanLoc = removeErrorFields(loc);
      // Converte zipCode para cep se necessário
      if (loc.zipCode && !cleanLoc.cep) {
        cleanLoc.cep = loc.zipCode;
        delete cleanLoc.zipCode;
      }
      return cleanLoc;
    });
  } else {
    cleanedCompany.locations = [];
  }
  
  // Garantir que support existe
  if (!cleanedCompany.support) {
    cleanedCompany.support = {};
  }
  
  // Clean policies items
  if (Array.isArray(cleanedCompany.policies)) {
    cleanedCompany.policies = cleanFieldsArray(cleanedCompany.policies);
  }
  
  return cleanedCompany;
};

const cleanUserFieldsData = cleanFieldsArray;
const cleanAdditionalInfo = cleanFieldsArray;

// Sistema de cache unificado
const CACHE_DURATION = 60000; // 1 minuto
const cache = {
  labels: { data: null, time: 0 },
  teams: { data: null, time: 0 }
};

const getCachedData = (type, getter) => {
  const now = Date.now();
  if (!cache[type].data || (now - cache[type].time) > CACHE_DURATION) {
    cache[type].data = store.getters[getter] || [];
    cache[type].time = now;
  }
  return cache[type].data;
};

// Função genérica para limpar campos de tags/times
const cleanEntityFieldsData = (fields, entityType, idField, nameField) => {
  if (!Array.isArray(fields)) return [];
  
  const entities = getCachedData(
    entityType,
    entityType === 'labels' ? 'labels/getLabels' : 'teams/getTeams'
  );
  
  // Criar mapa para busca O(1)
  const entitiesMap = new Map(entities.map(entity => [entity[idField], entity]));
  
  // Corrigir a chave de busca baseada no tipo
  const fieldIdKey = entityType === 'labels' ? 'tagId' : 'teamId';
  
  return fields
    .map(field => {
      const entity = entitiesMap.get(field[fieldIdKey]);
      if (!entity) return null;
      
      return {
        name: entity[nameField],
        description: field.description || ''
      };
    })
    .filter(Boolean);
};

const cleanTagFieldsData = (tagFields) => {
  return cleanEntityFieldsData(tagFields, 'labels', 'id', 'title');
};

const cleanTeamFieldsData = (teamFields) => {
  return cleanEntityFieldsData(teamFields, 'teams', 'id', 'name');
};

// Função genérica para transformar dados salvos para edição
const transformEntityFieldsForEditing = (savedFields, entityType, idField, nameField) => {
  if (!Array.isArray(savedFields)) {
    return [];
  }
  
  const entities = getCachedData(
    entityType,
    entityType === 'labels' ? 'labels/getLabels' : 'teams/getTeams'
  );
  
  // Criar mapa para busca O(1) por nome/título
  const entitiesByName = new Map(entities.map(entity => [entity[nameField], entity]));
  
  // Usar as chaves corretas baseadas no tipo
  const entityIdField = entityType === 'labels' ? 'tagId' : 'teamId';
  const errorField = entityType === 'labels' ? 'tagError' : 'teamError';
  
  return savedFields
    .map(savedField => {
      let entityId = savedField[entityIdField];
      let description = savedField.description || '';
      
      // Se não tem ID mas tem nome, buscar pelo nome
      if (!entityId && savedField.name) {
        const entity = entitiesByName.get(savedField.name);
        if (entity) {
          entityId = entity[idField];
        }
      }
      
      if (!entityId) return null;
      
      return {
        [entityIdField]: entityId,
        description,
        [errorField]: '',
        descriptionError: ''
      };
    })
    .filter(Boolean);
};

const transformTagFieldsForEditing = (savedTagFields) => {
  return transformEntityFieldsForEditing(savedTagFields, 'labels', 'id', 'title');
};

const transformTeamFieldsForEditing = (savedTeamFields) => {
  return transformEntityFieldsForEditing(savedTeamFields, 'teams', 'id', 'name');
};

// Methods
const nextStep = async () => {
  const canProceed = stepIsValid.value;

  if (!canProceed) {
    useAlert(t('Não pode prosseguir: validação falhou'), { type: 'error' });
    return;
  }

  if (activeStep.value >= totalSteps.value) {
    console.log('Último passo alcançado');
    return;
  }

  console.log(`Avançando para o passo ${activeStep.value + 1}`);

  // Prepara a transição
  isTransitioning.value = true;
  previousStep.value = activeStep.value;

  // Usa requestAnimationFrame para melhor performance
  await nextTick();
  requestAnimationFrame(() => {
    let nextStepNumber = activeStep.value + 1;

    // Se estamos no passo 9 e o fluxo não tem configurações específicas habilitadas
    // Habilitar para vendas (id 2) e agendamento (id 3)
    if (activeStep.value === 9 && agentData.conversationFlowId !== 2 && agentData.conversationFlowId !== 3) {
      // Não há mais passos após o 9 para fluxos sem configurações específicas
      return;
    }

    activeStep.value = nextStepNumber;

    // Atualiza o passo mais alto alcançado
    if (activeStep.value > highestStepReached.value) {
      highestStepReached.value = activeStep.value;
    }
    
    // Scroll e finalização em paralelo
    Promise.all([
      scrollToTop(),
      new Promise(resolve => setTimeout(resolve, 250))
    ]).then(() => {
      isTransitioning.value = false;
      checkScrollbar();
    });
  });
};

const prevStep = async () => {
  if (activeStep.value <= 1) return;
  
  isTransitioning.value = true;
  previousStep.value = activeStep.value;
  
  await nextTick();
  requestAnimationFrame(() => {
    activeStep.value -= 1;
    
    Promise.all([
      scrollToTop(),
      new Promise(resolve => setTimeout(resolve, 250))
    ]).then(() => {
      isTransitioning.value = false;
      checkScrollbar();
    });
  });
};

// Função para navegar diretamente para um passo específico
const goToStep = async (step) => {
  // Primeiro verifica se o passo é acessível
  if (!isStepAccessible(step)) {
    return;
  }
  
  // Se estamos tentando ir para um passo posterior
  if (step > activeStep.value) {
    // Valida todos os passos intermediários
    for (let i = activeStep.value; i < step; i++) {
      if (!validateStepByNumber(i)) {
        useAlert(`Você precisa completar o passo ${i} antes de continuar`, { type: 'error' });
        return;
      }
    }
  }
  
  // Se chegou aqui, pode navegar com animação
  isTransitioning.value = true;
  previousStep.value = activeStep.value;
  
  await nextTick();
  requestAnimationFrame(() => {
    activeStep.value = step;
    
    // Atualiza o passo mais alto alcançado se necessário
    if (step > highestStepReached.value && !isEditMode.value) {
      highestStepReached.value = step;
    }
    
    Promise.all([
      scrollToTop(),
      new Promise(resolve => setTimeout(resolve, 250))
    ]).then(() => {
      isTransitioning.value = false;
      checkScrollbar();
    });
  });
};

// Função auxiliar para validar um passo pelo número
const validateStepByNumber = (stepNumber) => {
  return validateStep(stepNumber);
};

// Verifica se um passo é acessível (pode ser clicado)
const isStepAccessible = (step) => {
  // No modo edição, todos os passos são acessíveis
  if (isEditMode.value) {
    return true;
  }
  
  // No modo criação, verifica se:
  // 1. É um passo já visitado (pode voltar)
  if (step <= activeStep.value) {
    return true;
  }
  
  // 2. É o próximo passo E o passo atual está válido
  if (step === activeStep.value + 1) {
    return validateStepByNumber(activeStep.value);
  }
  
  // 3. Qualquer outro caso, não é acessível
  return false;
};

// Verifica se um passo foi completado
const stepIsCompleted = (step) => {
  return validateStep(step) && step < activeStep.value;
};

// Função removida - passo 10 não existe mais

// Computed para largura da barra de progresso
const progressBarWidth = computed(() => {
  if (isEditMode.value) {
    return 100;
  }
  // Calcula baseado no passo atual
  // Mínimo de 10% para mostrar progresso desde o início
  const baseProgress = (activeStep.value / totalSteps.value) * 100;
  return Math.max(10, baseProgress);
});

// Função para obter classes do botão do passo
const getStepClasses = (step) => {
  const isActive = activeStep.value === step;
  const isCompleted = isEditMode.value || stepIsCompleted(step);
  const isAccessible = isStepAccessible(step);
  
  if (isActive) {
    return 'bg-primary-600 border-primary-600 text-white hover:bg-primary-700 hover:border-primary-700 transform scale-110 shadow-lg animate-pulse-once';
  }
  
  if (isCompleted) {
    return 'bg-white dark:bg-n-slate-2 border-botao-escuro border-primary-600 text-primary-600 hover:bg-primary-50 hover:border-primary-700 hover:scale-105';
  }
  
  if (isAccessible) {
    return 'bg-white dark:bg-n-slate-2 border-n-slate-4 text-n-slate-11 hover:border-primary-400 hover:text-primary-600 cursor-pointer hover:scale-105';
  }
  
  return 'bg-n-slate-2 border-n-slate-3 text-n-slate-8 cursor-not-allowed opacity-50';
};

// Função para obter classe do texto do passo
const getStepTextClass = (step) => {
  const isActive = activeStep.value === step;
  const isCompleted = isEditMode.value || stepIsCompleted(step);
  const isAccessible = isStepAccessible(step);
  
  if (isActive || isCompleted) {
    return 'text-n-slate-12';
  }
  
  if (isAccessible) {
    return 'text-n-slate-11';
  }
  
  return 'text-n-slate-8';
};

// Função para obter classe de animação do passo
const getStepAnimationClass = (step) => {
  if (isTransitioning.value) {
    return 'opacity-0'; // Hide during transition
  }
  
  if (step === activeStep.value) {
    // Determinar direção da animação baseado no passo anterior
    if (previousStep.value < activeStep.value) {
      return 'animate-fade-in-step-in'; // Vindo da esquerda (próximo)
    } else if (previousStep.value > activeStep.value) {
      return 'animate-fade-in-step-out'; // Vindo da direita (voltar)
    } else {
      return 'animate-fade-in'; // Primeira vez ou sem mudança
    }
  }
  
  return 'animate-fade-in';
};

const handleSubmit = async () => {
  if (!isFormValid.value) {
    useAlert('Campos obrigatórios', { type: 'error' });
    return;
  }

  isSubmitting.value = true;
  
  try {
    // Clean up data before sending to API
    const cleanCompany = cleanCompanyData(agentData.company);
    const cleanUserFields = cleanUserFieldsData(agentData.userFields);
    const cleanedAdditionalInfo = cleanAdditionalInfo(agentData.additionalInfo);
    const cleanedTagFields = cleanTagFieldsData(agentData.tagFields);
    const cleanedTeamFields = cleanTeamFieldsData(agentData.teamFields);
    
    // Garantir que a descrição está presente
    if (!cleanCompany.description && agentData.company.description) {
      cleanCompany.description = agentData.company.description;
    }
    
    // Prepare company_data object with all nested information
    const companyData = {
      cnpj: cleanCompany.cnpj || '',
      name: cleanCompany.name || '',
      role: agentData.role || '',
      support: {
        email: cleanCompany.support?.email || '',
        phone: cleanCompany.support?.phone || '',
        faqLink: cleanCompany.support?.faqLink || ''
      },
      website: cleanCompany.website || '',
      policies: cleanCompany.policies || [],
      locations: cleanCompany.locations || [],
      description: cleanCompany.description || '',
      additionalInfo: cleanedAdditionalInfo || [],
      tagFields: cleanedTagFields || [],
      teamFields: cleanedTeamFields || [],
      messageGrouping: agentData.messageGrouping !== undefined ? agentData.messageGrouping : 25,
      audioEnabled: agentData.audioEnabled !== undefined ? agentData.audioEnabled : true,
      randomness: agentData.randomness !== undefined ? agentData.randomness : 40,
      customImageInstructionsEnabled: agentData.customImageInstructionsEnabled !== undefined ? agentData.customImageInstructionsEnabled : false,
      imagePrompt: agentData.imagePrompt || '',
      humanRedirectConditions: agentData.humanRedirectConditions || '',
      humanAvailabilityMessagingEnabled: agentData.humanAvailabilityMessagingEnabled === true,
      humanAvailabilityHours: agentData.humanAvailabilityMessagingEnabled ? agentData.humanAvailabilityHours : null,
      // Configurações específicas do fluxo - simplificado para apenas shoppingCartEnabled
      shoppingCartEnabled: agentData.conversationFlowId === 2 ? (agentData.flowSpecificConfig?.shoppingCartEnabled || false) : null,
      // schedulingSettings removido - gerenciado no profissional
    };

    // Prepare data for API - must be wrapped in 'agent' object
    const apiData = {
      agent: {
        name: agentData.name,
        voice: agentData.voice,
        company: companyData,  // The API expects 'company', not 'company_data'
        userFields: cleanUserFields || [],  // The API expects 'userFields', not 'customer_data'
        personalityId: agentData.personalityId,  // The API expects 'personalityId'
        conversationFlowId: agentData.conversationFlowId  // The API expects 'conversationFlowId'
      }
    };
    
    // Get auth headers
    const headers = getAuthHeaders();
    
    let response;
    
    if (isEditMode.value && agentId.value) {
      // Update existing agent
      response = await axios.put(
        `/api/v1/agents_ai/${agentId.value}`,
        apiData,
        {
          headers,
          withCredentials: true
        }
      );
      
      console.log("Agente atualizado com sucesso");
      useAlert('Agente atualizado com sucesso');
    } else {
      // Create new agent
      response = await axios.post(
        '/api/v1/agents_ai', 
        apiData,
        {
          headers,
          withCredentials: true
        }
      );
      
      console.log("Agente criado com sucesso");
      useAlert('Agente criado com sucesso');
    }
    
    // Marcar como salvo antes de navegar
    hasUnsavedChanges.value = false;
    
    // Navigate to agent detail view page
    const agentIdToOpen = response.data?.id || (isEditMode.value ? agentId.value : null);
    
    if (agentIdToOpen) {
      // Navigate to agent detail view
      router.push({ 
        name: 'agentis_view',
        params: { 
          accountId: route.params.accountId,
          agentId: String(agentIdToOpen) 
        }
      });
    } else {
      // Se não conseguiu obter o ID do agente, vai para a lista
      router.push({ name: 'agentis_list' });
    }
  } catch (error) {
    console.error("Erro ao " + (isEditMode.value ? "atualizar" : "criar") + " agente:", error);
    
    // Show specific error message if available
    if (error.response && error.response.data) {
      if (error.response.data.error) {
        useAlert(error.response.data.error, { type: 'error' });
      } else if (error.response.data.errors) {
        // Handle validation errors
        const errorMessages = Object.entries(error.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('; ');
        useAlert(`Erros de validação: ${errorMessages}`, { type: 'error' });
      } else if (error.response.data.message) {
        useAlert(error.response.data.message, { type: 'error' });
      } else {
        useAlert(isEditMode.value ? 'Erro ao atualizar agente. Por favor, tente novamente.' : 'Erro ao criar agente. Por favor, tente novamente.', { type: 'error' });
      }
    } else {
      useAlert(isEditMode.value ? 'Erro ao atualizar agente. Por favor, tente novamente.' : 'Erro ao criar agente. Por favor, tente novamente.', { type: 'error' });
    }
  } finally {
    isSubmitting.value = false;
  }
};

// Função genérica para handlers de validação simples
const createValidationHandler = (validationRef) => (isValid) => {
  validationRef.value = isValid;
};

const handleCompanyValidation = createValidationHandler(companyInfoIsValid);
const handleUserFieldsValidation = createValidationHandler(userFieldsIsValid);

const updateAgentBasicInfo = (data) => {
  agentData.name = data.name;
  agentData.voice = data.voice;
  agentData.role = data.role; // Atualiza também a função do agente
  agentData.messageGrouping = data.messageGrouping !== undefined ? data.messageGrouping : 25; // Atualiza o tempo de agrupamento
  agentData.audioEnabled = data.audioEnabled !== undefined ? data.audioEnabled : true; // Atualiza audioEnabled
  agentData.randomness = data.randomness !== undefined ? data.randomness : 40; // Atualiza randomness
  agentData.customImageInstructionsEnabled = data.customImageInstructionsEnabled !== undefined ? data.customImageInstructionsEnabled : false; // Atualiza customImageInstructionsEnabled
  agentData.imagePrompt = data.imagePrompt !== undefined ? data.imagePrompt : ''; // Atualiza imagePrompt
  // Se data.isValid não estiver presente, não atualizamos o status
  if (data.isValid !== undefined) {
    agentNameIsValid.value = data.isValid;
  }
};

const updateCompanyInfo = (data) => {
  // Evita loops recursivos
  if (JSON.stringify(data) === JSON.stringify(agentData.company)) {
    return;
  }
  
  // Preservar a descrição se não estiver no objeto recebido
  if (!data.description && agentData.company.description) {
    data.description = agentData.company.description;
  }
  
  // Atualizar cada campo individualmente para preservar dados existentes
  Object.assign(agentData.company, data);
};

const updateUserFields = (data) => {
  agentData.userFields = data;
};

const selectPersonality = (id) => {
  agentData.personalityId = id;
};

const selectConversationFlow = (id) => {
  agentData.conversationFlowId = id;
};

const updateFlowSpecificConfig = (config) => {
  agentData.flowSpecificConfig = config;
};

const handleFlowSpecificConfigValidation = (isValid) => {
  flowSpecificConfigIsValid.value = isValid;
};

// Funções para labels dos passos
const getStepLabel = (step) => {
  if (step === 10) {
    switch(agentData.conversationFlowId) {
      case 2:
        return 'Carrinho de Compras';
      case 3:
        return 'Configurar Agenda';
      default:
        return 'Configurações';
    }
  }
  return '';
};

const getStepShortLabel = (step) => {
  if (step === 10) {
    switch(agentData.conversationFlowId) {
      case 2:
        return 'Carrinho';
      case 3:
        return 'Agenda';
      default:
        return 'Config';
    }
  }
  return '';
};

// Funções auxiliares para título e descrição dinâmicos das configurações específicas do fluxo
const getFlowSpecificConfigTitle = () => {
  switch(agentData.conversationFlowId) {
    case 1:
      return 'Configurações de Qualificação';
    case 2:
      return 'Configurações de Vendas';
    case 3:
      return 'Configurações de Agendamento';
    default:
      return 'Configurações Específicas';
  }
};

const getFlowSpecificConfigDescription = () => {
  switch(agentData.conversationFlowId) {
    case 1:
      return 'Configure como o agente realizará a qualificação de leads e coleta de informações dos clientes.';
    case 2:
      return 'Configure o carrinho de compras e outras funcionalidades relacionadas ao processo de vendas.';
    case 3:
      return 'Configure como o agente gerenciará agendamentos e disponibilidade de horários.';
    default:
      return 'Configure as funcionalidades específicas para este tipo de fluxo de conversa.';
  }
};

// Funções de agendamento removidas - gerenciado no profissional
const handleAdditionalInfoValidation = createValidationHandler(additionalInfoIsValid);

const updateAdditionalInfo = (data) => {
  agentData.additionalInfo = data;
};

// Otimização para atualização de tags
const updateTagFields = (data) => {
  // Evita atualizações desnecessárias
  if (JSON.stringify(agentData.tagFields) !== JSON.stringify(data)) {
    agentData.tagFields = data;
  }
};

const handleTagFieldsValidation = createValidationHandler(tagsDataIsValid);
const handleTeamFieldsValidation = createValidationHandler(teamsDataIsValid);

// Funções para atualização de times
const updateTeamFields = (data) => {
  // Evita atualizações desnecessárias
  if (JSON.stringify(agentData.teamFields) !== JSON.stringify(data)) {
    agentData.teamFields = data;
  }
};

const updateHumanRedirectConditions = (data) => {
  if (typeof data === 'string') {
    // Backward compatibility: if receiving just a string, it's the old format
    agentData.humanRedirectConditions = data;
  } else {
    // New format with schedule data
    agentData.humanRedirectConditions = data.redirectConditions || '';
    agentData.humanAvailabilityMessagingEnabled = data.humanAvailabilityMessagingEnabled === true;
    // Only update humanAvailabilityHours if humanAvailabilityMessagingEnabled is true
    if (data.humanAvailabilityMessagingEnabled && data.humanAvailabilityHours) {
      agentData.humanAvailabilityHours = data.humanAvailabilityHours;
    } else if (!data.humanAvailabilityMessagingEnabled) {
      // Reset to default if disabled
      agentData.humanAvailabilityHours = {
        sunday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
        monday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
        tuesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
        wednesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
        thursday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
        friday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
        saturday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] }
      };
    }
  }
};

// Métodos do passo 10 removidos - passo não existe mais

// Função otimizada para iniciar a criação do agente
const startAgentCreation = async () => {
  isTransitioning.value = true;
  
  await nextTick();
  requestAnimationFrame(() => {
    showInitialPage.value = false;
    isFormStarted.value = true;
    hasUnsavedChanges.value = false;
    
    Promise.all([
      scrollToTop(),
      new Promise(resolve => setTimeout(resolve, 250))
    ]).then(() => {
      isTransitioning.value = false;
      checkScrollbar();
    });
  });
};

// Funções para confirmação de saída
const confirmExit = async () => {
  const targetRoute = pendingRoute.value;
  
  // Reset all states before navigation
  showExitConfirmation.value = false;
  hasUnsavedChanges.value = false;
  isFormStarted.value = false;
  pendingRoute.value = null;
  
  // Use nextTick to ensure state updates are processed before navigation
  await nextTick();
  
  if (targetRoute) {
    router.push(targetRoute);
  }
};

const cancelExit = () => {
  showExitConfirmation.value = false;
  pendingRoute.value = null;
};

// Guard de navegação simplificado
onBeforeRouteLeave((to, _, next) => {
  // Verificar se precisa confirmação
  const needsConfirmation = isFormStarted.value && 
                           hasUnsavedChanges.value && 
                           !isSubmitting.value && 
                           !showInitialPage.value;
  
  if (!needsConfirmation) {
    next();
    return;
  }
  
  // Se já está mostrando confirmação, bloqueia navegação
  if (showExitConfirmation.value) {
    next(false);
    return;
  }
  
  // Armazena a rota pendente e mostra confirmação
  pendingRoute.value = to;
  showExitConfirmation.value = true;
  next(false);
});

// Listener para fechamento de aba/janela
const handleBeforeUnload = (e) => {
  if (isFormStarted.value && hasUnsavedChanges.value) {
    e.preventDefault();
    e.returnValue = 'Você tem alterações não salvas. Deseja realmente sair?';
    return e.returnValue;
  }
};


// Watchers para detectar mudanças com debounce
let changeDebounceTimer = null;

watch(agentData, () => {
  // Avoid marking as changed during initial data load
  if (isSettingInitialData || !isFormStarted.value || showInitialPage.value) {
    return;
  }
  
  // Debounce para evitar múltiplas atualizações
  clearTimeout(changeDebounceTimer);
  changeDebounceTimer = setTimeout(() => {
    hasUnsavedChanges.value = true;
  }, 300);
}, { deep: true });

// Watch activeStep changes com otimização
let scrollbarCheckTimer = null;
watch(activeStep, () => {
  // Cancela check anterior se houver
  clearTimeout(scrollbarCheckTimer);
  
  // Agenda novo check com pequeno delay
  scrollbarCheckTimer = setTimeout(() => {
    checkScrollbar();
  }, 50);
});

// Carregar dados do agente quando estiver no modo de edição
onMounted(async () => {
  // Adicionar listener para fechamento de aba
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Carregar labels e teams primeiro, necessário para processar as tags e times
  // Carrega em paralelo se possível
  const labelsPromise = store.dispatch('labels/get');
  const teamsPromise = store.dispatch('teams/get');
  
  // Aguarda ambas as promises
  await Promise.all([labelsPromise, teamsPromise]);
  
  if (isEditMode.value) {
    // No modo de edição, pula a página inicial
    showInitialPage.value = false;
    isFormStarted.value = true;
    
    // Set flag before loading data
    isSettingInitialData = true;
    
    await nextTick(); // Aguarda os componentes serem montados
    loadAgentForEditing();
    await nextTick(); // Aguarda a atualização dos dados
    
    // Forçar re-validação após carregamento
    agentNameIsValid.value = !!agentData.name && agentData.name.length >= 3;
    companyInfoIsValid.value = !!agentData.company.name && !!agentData.company.description;
    
    // Reset flags after initial load
    setTimeout(() => {
      isSettingInitialData = false;
      hasUnsavedChanges.value = false;
    }, 100);
  }
  
  // Disable initial load state after animations complete
  setTimeout(() => {
    isInitialLoad.value = false;
  }, 500);
  
  nextTick(() => {
    checkScrollbar();
    
    // Observar mudanças no tamanho do conteúdo
    if (scrollContainer.value) {
      resizeObserver = new ResizeObserver(() => {
        checkScrollbar();
      });
      resizeObserver.observe(scrollContainer.value);
    }
  });
});

// Variável para armazenar o ResizeObserver
let resizeObserver = null;

// Estado para controlar o modal de informação dinâmico
const showInfoModal = ref(false);
const infoModalConfig = ref({
  type: '', // 'attributes', 'tags', 'teams'
  title: '',
  description: '',
  howItWorks: [],
  importantNote: '',
  buttonLabel: '',
  settingsPath: ''
});

// Função genérica para abrir o modal de informação
const openInfoModal = (type) => {
  const accountId = route.params.accountId;
  
  switch(type) {
    case 'attributes':
      infoModalConfig.value = {
        type: 'attributes',
        title: 'Gerenciar Atributos Personalizados',
        description: 'Os atributos personalizados permitem que você colete informações específicas dos seus clientes durante as conversas.',
        howItWorks: [
          'Os atributos criados aqui serão coletados pelo agente durante a conversa',
          'Você pode marcar atributos como obrigatórios ou opcionais',
          'Para editar ou criar novos tipos de atributos, acesse as Configurações'
        ],
        importantNote: 'Para criar ou editar os tipos de atributos disponíveis no sistema, você precisa acessar a página de Configurações.',
        buttonLabel: 'Ir para Configurações de Atributos',
        settingsPath: `/app/accounts/${accountId}/settings/custom-attributes/list?tab=contact`
      };
      break;
    case 'tags':
      infoModalConfig.value = {
        type: 'tags',
        title: 'Gerenciar Etiquetas',
        description: 'As etiquetas ajudam a categorizar e organizar as conversas automaticamente.',
        howItWorks: [
          'Configure quando cada etiqueta deve ser aplicada automaticamente',
          'O agente aplicará as etiquetas baseado no contexto da conversa',
          'Para criar ou editar etiquetas, acesse as Configurações'
        ],
        importantNote: 'Para criar, editar ou excluir etiquetas do sistema, você precisa acessar a página de Configurações.',
        buttonLabel: 'Ir para Etiquetas',
        settingsPath: `/app/accounts/${accountId}/settings/labels`
      };
      break;
    case 'teams':
      infoModalConfig.value = {
        type: 'teams',
        title: 'Gerenciar Times',
        description: 'Configure como o agente deve atribuir conversas aos times corretos.',
        howItWorks: [
          'Defina contextos específicos para cada time',
          'O agente atribuirá conversas automaticamente baseado nas regras definidas',
          'Para criar ou editar times, acesse as Configurações'
        ],
        importantNote: 'Para criar, editar ou gerenciar times e seus membros, você precisa acessar a página de Configurações.',
        buttonLabel: 'Ir para Times',
        settingsPath: `/app/accounts/${accountId}/settings/teams`
      };
      break;
    case 'additionalInfo':
      infoModalConfig.value = {
        type: 'additionalInfo',
        title: 'Base de Conhecimento',
        description: 'As informações complementares ajudam o agente a ter contexto adicional sobre o fluxo da conversa, instruções e comportamento.',
        howItWorks: [
          'Adicione contexto adicional para o agente',
          'Inclua instruções específicas para melhorar as respostas',
          'Ajustes casos necessário para o comportamento desejado'
        ],
        importantNote: 'Essas informações são essenciais para que o agente possa oferecer um atendimento completo e preciso aos clientes.',
        buttonLabel: '',
        settingsPath: ''
      };
      break;
  }
  
  showInfoModal.value = true;
};

// Função para fechar o modal de informação
const closeInfoModal = () => {
  showInfoModal.value = false;
};

// Função para abrir a página de configurações em nova aba
const openSettingsPage = () => {
  if (infoModalConfig.value.settingsPath) {
    window.open(infoModalConfig.value.settingsPath, '_blank');
  }
};

// Remover listeners ao desmontar
onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
  
  // Limpar timers pendentes
  clearTimeout(changeDebounceTimer);
  clearTimeout(scrollbarCheckTimer);
  
  // Desconectar ResizeObserver se existir
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

// Função otimizada para scroll para o topo
const scrollToTop = () => {
  // Não precisa de await nextTick se não depende do DOM atualizado
  const target = scrollContainer.value || window;
  target.scrollTo?.(0, 0); // Usa scrollTo simples para melhor performance
};

// Ref para o elemento com scroll
const scrollContainer = ref(null);

// Função otimizada para verificar scrollbar com throttle
let lastScrollHeight = 0;
let checkScrollbarFrame = null;

const checkScrollbar = () => {
  if (checkScrollbarFrame) return; // Já tem uma verificação pendente
  
  checkScrollbarFrame = requestAnimationFrame(() => {
    if (!scrollContainer.value) {
      checkScrollbarFrame = null;
      return;
    }
    
    const { scrollHeight, clientHeight } = scrollContainer.value;
    
    // Só atualiza se houve mudança significativa
    if (Math.abs(scrollHeight - lastScrollHeight) > 5) {
      const hasScrollbar = scrollHeight > clientHeight;
      scrollContainer.value.style.paddingRight = hasScrollbar ? '8px' : '0';
      lastScrollHeight = scrollHeight;
    }
    
    checkScrollbarFrame = null;
  });
};
</script>

<style scoped>
/* Existing shimmer animation */
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}

/* Fade animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInStepIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInStepOut {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in-scale {
  animation: fadeInScale 0.3s ease-in-out;
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out forwards;
}

.animate-fade-in-step-in {
  animation: fadeInStepIn 0.3s ease-out forwards;
}

.animate-fade-in-step-out {
  animation: fadeInStepOut 0.3s ease-out forwards;
}

.animate-fade-out {
  animation: fadeOut 0.3s ease-in forwards;
}

/* Slide animations */
@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-right {
  animation: slideInRight 0.4s ease-out forwards;
}

.animate-slide-left {
  animation: slideInLeft 0.4s ease-out forwards;
}

/* Scale animation for buttons */
@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0.5;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scale {
  animation: scaleIn 0.2s ease-out;
}

/* Pulse animation for active step */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

.animate-pulse-once {
  animation: pulse 0.5s ease-in-out;
}

/* Progress bar animation */
.progress-bar-animated {
  position: relative;
  overflow: hidden;
}

.progress-bar-animated::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transform: translateX(-100%);
  animation: shimmer 2s infinite;
}

/* Transition classes for Vue */
.step-enter-active,
.step-leave-active {
  transition: all 0.3s ease;
}

.step-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.step-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Smooth height transitions */
.height-transition {
  transition: height 0.3s ease-out;
}
</style>

<template>
  <div class="w-full h-full mx-auto bg-n-background flex flex-col">
    <!-- Header -->
    <header
      class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-n-background border-b border-n-weak pl-6 flex-shrink-0">
      <div class="flex justify- items-center gap-2">
        <woot-sidemenu-icon v-if="showSidemenuIcon" />
        <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
          {{ isEditMode ? 'Editar Agente' : 'Criar Novo Agente' }}
        </h1>
      </div>
    </header>
    
    <!-- conteudo -->
    <div ref="scrollContainer" class="flex-1 overflow-y-auto custom-scrollbar">
      <!-- Página Inicial CTA (apenas no modo criação) -->
      <div v-if="showInitialPage && !isEditMode" class="w-full min-h-[calc(100vh-3.5rem)] flex items-center justify-center bg-gradient-to-br from-n-slate-1 to-n-slate-2 py-8">
        <div class="max-w-2xl mx-auto px-4 sm:px-6 text-center animate-fade-in-scale">
          <!-- Ícone animado -->
          <div class="mb-6 sm:mb-8 inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-n-iris-3 rounded-full animate-pulse">
            <svg class="w-12 h-12 text-n-iris-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z"></path>
            </svg>
          </div>
          
          <!-- Título principal -->
          <h1 class="text-3xl sm:text-4xl font-bold text-n-slate-12 mb-4 font-interDisplay tracking-tight">
            Crie seu Agente Inteligente
          </h1>
          
          <!-- Descrição -->
          <p class="text-base sm:text-lg text-n-slate-11 mb-6 sm:mb-8 leading-relaxed max-w-xl mx-auto">
            Configure um assistente virtual personalizado para sua empresa. 
            Defina personalidade, fluxos de conversa e informações específicas 
            para oferecer um atendimento excepcional aos seus clientes.
          </p>
          
          <!-- Features em destaque -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 sm:mb-10 max-w-xl mx-auto">
            <div class="flex flex-col items-center p-4">
              <div class="w-10 h-10 bg-n-teal-3 rounded-lg flex items-center justify-center mb-2">
                <svg class="w-6 h-6 text-n-teal-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <span class="text-sm font-medium text-n-slate-12">Configuração<br>Rápida</span>
            </div>
            
            <div class="flex flex-col items-center p-4">
              <div class="w-10 h-10 bg-n-amber-3 rounded-lg flex items-center justify-center mb-2">
                <svg class="w-6 h-6 text-n-amber-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
                </svg>
              </div>
              <span class="text-sm font-medium text-n-slate-12">Totalmente<br>Personalizável</span>
            </div>
            
            <div class="flex flex-col items-center p-4">
              <div class="w-10 h-10 bg-n-ruby-3 rounded-lg flex items-center justify-center mb-2">
                <svg class="w-6 h-6 text-n-ruby-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                </svg>
              </div>
              <span class="text-sm font-medium text-n-slate-12">Atendimento<br>Humanizado</span>
            </div>
          </div>
          
          <!-- Botão CTA -->
          <Button 
            label="Começar a Criar Agente" 
            color="blue" 
            size="lg"
            class="min-w-[250px] shadow-lg hover:shadow-xl transition-shadow"
            @click="startAgentCreation" 
          />
          
          <!-- Tempo estimado -->
          <p class="mt-6 text-sm text-n-slate-10">
            <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Tempo estimado: 5-15 minutos
          </p>
        </div>
      </div>
      
      <!-- Formulário de criação/edição -->
      <div v-show="!showInitialPage" class="w-full mx-auto max-w-6xl p-4 sm:p-6 lg:p-8">
        
        <!-- Barra de navegação de passos -->
        <div class="mb-6 sm:mb-8 bg-white dark:bg-n-slate-2 rounded-xl shadow-sm border border-n-slate-3 p-4 sm:p-6">
          <!-- Título da seção -->
          <div class="mb-6">
            <h3 class="text-sm font-medium text-n-slate-11 uppercase tracking-wider">Etapas da Configuração</h3>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ isEditMode ? 'Clique em qualquer etapa para editar' : 'Complete cada etapa para criar seu agente' }}
            </p>
          </div>
          
          <div class="relative py-2">
            <!-- Linha de progresso de fundo -->
            <div class="absolute top-7 left-0 right-0 h-1 bg-n-slate-3 rounded-full"></div>
            
            <!-- Linha de progresso ativa -->
            <div 
              class="absolute top-7 left-0 h-1 bg-primary-600 rounded-full transition-transform duration-300 ease-out progress-bar-animated"
              :style="{ width: `${progressBarWidth}%` }"
            ></div>
            
            <!-- Container dos passos -->
            <div :class="[
              'relative grid gap-1 sm:gap-2 lg:gap-4 overflow-x-auto scrollbar-hide',
              totalSteps === 10 ? 'grid-cols-10' : 'grid-cols-9'
            ]">
              <!-- Passo 1 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(1)
                    ]"
                    :disabled="!isStepAccessible(1)"
                    @click="isStepAccessible(1) && goToStep(1)"
                  >
                  <svg v-if="isEditMode || stepIsCompleted(1)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span v-else class="text-xs sm:text-sm font-semibold">1</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(1)">Informações Básicas</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(1)">Básico</span>
              </div>
              
              <!-- Passo 2 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(2)
                    ]"
                    :disabled="!isStepAccessible(2)"
                    @click="isStepAccessible(2) && goToStep(2)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(2)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">2</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(2)">Redirecionamento Humano</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(2)">Redirecionar</span>
              </div>
              
              <!-- Passo 3 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(3)
                    ]"
                    :disabled="!isStepAccessible(3)"
                    @click="isStepAccessible(3) && goToStep(3)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(3)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">3</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(3)">Dados da Empresa</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(3)">Empresa</span>
              </div>
              
              <!-- Passo 4 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(4)
                    ]"
                    :disabled="!isStepAccessible(4)"
                    @click="isStepAccessible(4) && goToStep(4)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(4)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">4</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(4)">Base de Conhecimento</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(4)">Contexto</span>
              </div>
              
              <!-- Passo 5 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(5)
                    ]"
                    :disabled="!isStepAccessible(5)"
                    @click="isStepAccessible(5) && goToStep(5)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(5)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">5</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(5)">Configuração de Etiquetas</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(5)">Etiquetas</span>
              </div>
              
              <!-- Passo 6 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(6)
                    ]"
                    :disabled="!isStepAccessible(6)"
                    @click="isStepAccessible(6) && goToStep(6)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(6)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">6</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(6)">Atribuição de Times</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(6)">Times</span>
              </div>
              
              <!-- Passo 7 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(7)
                    ]"
                    :disabled="!isStepAccessible(7)"
                    @click="isStepAccessible(7) && goToStep(7)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(7)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">7</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(7)">Atributos do Usuário</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(7)">Cliente</span>
              </div>
              
              <!-- Passo 8 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(8)
                    ]"
                    :disabled="!isStepAccessible(8)"
                    @click="isStepAccessible(8) && goToStep(8)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(8)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">8</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(8)">Personalidade do Agente</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(8)">Personalidade</span>
              </div>
              
              <!-- Passo 9 -->
              <div class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(9)
                    ]"
                    :disabled="!isStepAccessible(9)"
                    @click="isStepAccessible(9) && goToStep(9)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(9)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">9</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(9)">Fluxo de Conversa</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(9)">Fluxo</span>
              </div>

              <!-- Passo 10 - Configurações Específicas (aparece apenas quando fluxo de vendas é selecionado) -->
              <div v-if="totalSteps === 10" class="flex flex-col items-center">
                <div class="p-1">
                  <button
                    :class="[
                      'relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center',
                      getStepClasses(10)
                    ]"
                    :disabled="!isStepAccessible(10)"
                    @click="isStepAccessible(10) && goToStep(10)"
                  >
                    <svg v-if="isEditMode || stepIsCompleted(10)" class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span v-else class="text-xs sm:text-sm font-semibold">10</span>
                  </button>
                </div>
                <span class="mt-2 text-xs font-medium text-center hidden lg:block" :class="getStepTextClass(10)">{{ getStepLabel(10) }}</span>
                <span class="mt-2 text-xs font-medium lg:hidden" :class="getStepTextClass(10)">{{ getStepShortLabel(10) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 1: Basic Info -->
        <div v-if="activeStep === 1" :class="getStepAnimationClass(1)">
          <FormSection title="Informações Básicas do Agente"
            description="Defina o nome, função e características de voz do seu agente. O nome será usado para identificar o agente nas conversas, e a função ajudará a definir como ele se apresentará aos clientes.">
            <AgentBasicInfo 
              :initial-name="agentData.name" 
              :initial-voice="agentData.voice" 
              :initial-role="agentData.role"
              :initial-message-grouping="agentData.messageGrouping"
              :initial-audio-enabled="agentData.audioEnabled"
              :initial-randomness="agentData.randomness"
              :initial-custom-image-instructions-enabled="agentData.customImageInstructionsEnabled"
              :initial-image-prompt="agentData.imagePrompt"
              :name-error="validationErrors.name" 
              :role-error="validationErrors.role" 
              @update="updateAgentBasicInfo"
              @validation="(isValid) => agentNameIsValid = isValid" />
          </FormSection>
        </div>

        <!-- Step 2: Human Redirect Conditions -->
        <div v-if="activeStep === 2" :class="getStepAnimationClass(2)">
          <FormSection title="Redirecionamento para Atendente Humano"
            description="Configure quando o agente deve transferir a conversa para um atendente humano. Este campo é opcional, mas pode melhorar a experiência do cliente ao definir situações específicas para redirecionamento.">
            <HumanRedirectField
              :initial-redirect-conditions="agentData.humanRedirectConditions"
              :initial-human-availability-messaging-enabled="agentData.humanAvailabilityMessagingEnabled"
              :initial-human-availability-hours="agentData.humanAvailabilityHours"
              @update="updateHumanRedirectConditions"
              @validation="(isValid) => humanRedirectIsValid = isValid"
            />
          </FormSection>
        </div>

        <!-- Step 3: Company Info -->
        <div v-if="activeStep === 3" :class="getStepAnimationClass(3)">
          <FormSection title="Informações da empresa"
            description="Forneça detalhes completos sobre sua empresa. Quanto mais informações você fornecer, melhor o agente poderá responder perguntas sobre seus produtos, serviços e políticas. Inclua CNPJ se disponível para maior credibilidade.">
            <CompanyInfo 
              :initial-company="agentData.company" 
              :validation-errors="validationErrors.company"
              @update="updateCompanyInfo" 
              @validation="handleCompanyValidation" />
          </FormSection>
        </div>

        <!-- Step 4: Additional Info -->
        <div v-if="activeStep === 4" :class="getStepAnimationClass(4)">
          <FormSection title="Base de Conhecimento"
            description="Inclua detalhes extras que possam ajudar o agente a oferecer um atendimento mais completo, como diferenciais, procedimentos internos, orientações específicas ou informações relevantes sobre produtos e serviços.">
            <template v-if="!isTransitioning">
              <AdditionalInfo
                :initial-additional-info="agentData.additionalInfo"
                @update="updateAdditionalInfo"
                @validation="handleAdditionalInfoValidation"
                @show-info="() => openInfoModal('additionalInfo')" />
            </template>
          </FormSection>
        </div>

        <!-- Step 5: Tags Data Fields -->
        <div v-if="activeStep === 5" :class="getStepAnimationClass(5)">
          <FormSection title="Configuração de Etiquetas"
            description="Configure como o agente deve utilizar as etiquetas existentes no sistema. Descreva em que situações cada tag deve ser aplicada automaticamente durante as conversas.">
            <template v-if="!isTransitioning">
              <TagsDataFieldsWrapper 
                :initial-tag-fields="agentData.tagFields" 
                :is-edit-mode="isEditMode"
                @update="updateTagFields"
                @validation="handleTagFieldsValidation"
                @show-tag-info="() => openInfoModal('tags')" />
            </template>
          </FormSection>
        </div>

        <!-- Step 6: Teams Data Fields -->
        <div v-if="activeStep === 6" :class="getStepAnimationClass(6)">
          <FormSection title="Atribuição de Times"
            description="Configure quando e para quais times o agente deve atribuir conversas. Defina contextos específicos para cada time melhorar a organização e distribuição do atendimento.">
            <template v-if="!isTransitioning">
              <TeamsDataFieldsWrapper 
                :initial-team-fields="agentData.teamFields" 
                :is-edit-mode="isEditMode"
                @update="updateTeamFields"
                @validation="handleTeamFieldsValidation"
                @show-team-info="() => openInfoModal('teams')" />
            </template>
          </FormSection>
        </div>

        <!-- Step 7: User Data Fields com validação -->
        <div v-if="activeStep === 7" :class="getStepAnimationClass(7)">
          <FormSection title="Atributos do Usuário"
            description="Configure os atributos que o agente deve coletar dos usuários durante as conversas. Marque como obrigatório apenas os atributos essenciais para não criar barreiras no atendimento.">
            <template v-if="!isTransitioning">
              <UserDataFieldsWrapper 
                :initial-user-fields="agentData.userFields" 
                :is-edit-mode="isEditMode"
                @update="updateUserFields"
                @validation="handleUserFieldsValidation"
                @show-attribute-info="() => openInfoModal('attributes')" />
            </template>
          </FormSection>
        </div>

        <!-- Step 8: Personality -->
        <div v-if="activeStep === 8" :class="getStepAnimationClass(8)">
          <FormSection title="Personalidade do Agente"
            description="Escolha a personalidade que melhor representa como você deseja que seu agente interaja com os clientes. Cada personalidade tem características únicas de comunicação e abordagem no atendimento.">
            <PersonalitySelector 
              :initial-personality-id="agentData.personalityId" 
              :company-name="agentData.company.name"
              :agent-name="agentData.name"
              :current-step="activeStep"
              @select="selectPersonality" />
          </FormSection>
        </div>

        <!-- Step 9: Conversation Flow -->
        <div v-if="activeStep === 9" :class="getStepAnimationClass(9)">
          <FormSection title="Fluxo de conversa"
            description="Defina a estrutura e o ritmo das conversas. Cada fluxo determina como o agente conduzirá a interação, quando coletar informações e como direcionar o atendimento. Escolha o fluxo que melhor se adequa ao seu tipo de negócio.">
            <ConversationFlowSelector
              :initial-flow-id="agentData.conversationFlowId"
              :current-step="activeStep"
              @select="selectConversationFlow" />
          </FormSection>
        </div>

        <!-- Step 10: Configurações Específicas do Fluxo -->
        <!-- Habilitado para fluxo de vendas (id 2) e agendamento (id 3) -->
        <div v-if="activeStep === 10 && (agentData.conversationFlowId === 2 || agentData.conversationFlowId === 3)" :class="getStepAnimationClass(10)">
          <FormSection
            :title="getFlowSpecificConfigTitle()"
            :description="getFlowSpecificConfigDescription()">
            <FlowSpecificConfig
              :flow-type="agentData.conversationFlowId"
              :initial-config="agentData.flowSpecificConfig"
              @update="updateFlowSpecificConfig"
              @valid="handleFlowSpecificConfigValidation" />
          </FormSection>
        </div>

        <!-- Botões de navegação com logs para depuração -->
        <div class="flex flex-col sm:flex-row justify-between gap-4 mt-6 sm:mt-8 mb-10">
          <Button v-if="activeStep > 1" label="Voltar" variant="outline" color="slate" :disabled="isTransitioning" @click="prevStep" class="w-full sm:w-auto" />
          <div v-else class="hidden sm:block"></div> <!-- Spacer para flex justify-between -->
          <div class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <!-- Botão de Salvar e Fechar no modo de edição (disponível em todas as etapas) -->
            <Button v-if="isEditMode && activeStep < totalSteps"
              label="Salvar e Fechar"
              color="teal"
              :is-loading="isSubmitting"
              :disabled="!isFormValid || isTransitioning"
              @click="handleSubmit"
              class="w-full sm:w-auto" />

            <Button v-if="activeStep < totalSteps" label="Próximo" color="blue" :disabled="!stepIsValid || isTransitioning"
              @click="nextStep" class="w-full sm:w-auto" />
            <Button v-else :label="isEditMode ? 'Atualizar Agente' : 'Criar Agente'" color="blue"
              :is-loading="isSubmitting" :disabled="!isFormValid || isTransitioning" @click="handleSubmit" class="w-full sm:w-auto" />
          </div>
        </div>
      </div>
      
      <!-- Modal de confirma\u00e7\u00e3o de sa\u00edda -->
      <AlertResponza
        :show="showExitConfirmation"
        type="warning-confirmation"
        title="Deseja sair sem salvar?"
        :message="isEditMode ? 'Você tem alterações não salvas no agente. Se sair agora, todas as modificações serão perdidas.' : 'Você tem um agente em criação com informações não salvas. Se sair agora, todos os dados inseridos serão perdidos.'"
        confirm-text="Sair sem salvar"
        @confirm="confirmExit"
        @cancel="cancelExit"
        @close="cancelExit"
      />
    </div>
    
    <!-- Modal de Informações Dinâmico (Atributos, Times, Etiquetas) -->
    <div v-if="showInfoModal" class="fixed inset-0 z-[9999] overflow-y-auto">
      <!-- Overlay -->
      <div class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-[4px] transition-opacity" @click="closeInfoModal"></div>
      
      <!-- Modal -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-md transform rounded-lg bg-white dark:bg-n-slate-2 p-6 shadow-2xl transition-all">
          <!-- Cabeçalho -->
          <div class="mb-4">
            <h3 class="text-lg font-semibold text-n-slate-12 flex items-center">
              <svg class="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              {{ infoModalConfig.title }}
            </h3>
          </div>
          
          <!-- Conteúdo -->
          <div class="space-y-4 text-sm text-n-slate-11">
            <p>{{ infoModalConfig.description }}</p>
            
            <div class="bg-n-slate-1 dark:bg-n-slate-3 rounded-lg p-4 border border-n-slate-4">
              <h4 class="font-medium text-n-slate-12 mb-2">Como funciona:</h4>
              <ul class="space-y-2 ml-4">
                <li v-for="(item, index) in infoModalConfig.howItWorks" :key="index" class="flex items-start">
                  <span class="text-primary-600 mr-2">•</span>
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
            
            <div class="bg-amber-50 dark:bg-amber-100 border border-amber-200 dark:border-amber-600 rounded-lg p-3">
              <p class="text-amber-800 dark:text-amber-700 flex items-start">
                <svg class="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
                <span>
                  <strong>Importante:</strong> {{ infoModalConfig.importantNote }}
                </span>
              </p>
            </div>
          </div>
          
          <!-- Botões de ação -->
          <div class="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              v-if="infoModalConfig.buttonLabel"
              :label="infoModalConfig.buttonLabel"
              color="blue"
              class="w-full sm:flex-[3]"
              @click="openSettingsPage"
            >
              <template #icon>
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </template>
            </Button>
            <Button
              label="Fechar"
              variant="outline"
              color="slate"
              class="w-full sm:flex-[3]"
              @click="closeInfoModal"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.border-botao-escuro{
  border-color: rgb(var(--color-primary-600) / var(--tw-border-opacity, 1)) !important
}

/* Ocultar barra de rolagem mas manter funcionalidade */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE e Edge */
  scrollbar-width: none;  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari e Opera */
}

/* Barra de rolagem personalizada para o container principal */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  scrollbar-gutter: stable; /* Reserva espaço para scrollbar mesmo quando não visível */
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: background-color 0.3s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Tema escuro */
.dark .custom-scrollbar {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(255, 255, 255, 0.4);
}

</style>